import { doc, getDoc } from 'firebase/firestore'
import {
  bridgePatientProjectionSchema,
  bridgePolicySchema,
  isBridgePolicyValidatedWithinReuseWindow,
  type BridgePatientProjection,
  type BridgePolicy,
  type BridgePolicyId,
  type CustomerId,
} from '@gh/common/models'
import { firestore } from '@/firebase'
import { bridgeCallables } from '@/bridge/bridgeCallables'
import { bridgePolicyPayerDisplayName } from '@/bridge/bridgePolicyDisplay'
import { platformCoordApi } from '@/components/cloudFunctions/platformCoord'
import { raceAbort } from '@/bridge/bridgePolicyCreateRetry'
import { waitForBridgePatientPolicyId } from '@/bridge/bridgePatientProjectionWait'
import { subscribeBridgePolicy } from '@/bridge/bridgePolicySubscription'
import { isBridgePolicyTerminalStatus } from '@/bridge/bridgePublishableClient'
import { emitClinicalBookingFact } from '@/components/consultation/bookingFacts/emitClinicalBookingFact'
import { buildBridgePolicyBookingFactRefs } from '@/components/consultation/bookingFacts/buildBridgePolicyBookingFactRefs'
import { emitConsentsBookingFactIfReady } from '@/components/consultation/bookingFacts/emitConsentsBookingFact'

const DEFAULT_POLICY_CONFIRM_TIMEOUT_MS = 60_000
const DEFAULT_POLICY_REMIT_INTERVAL_MS = 2_000

export function isBridgePatientProjectionLocallyReusable(
  projection: BridgePatientProjection,
): projection is BridgePatientProjection & { policyId: BridgePolicyId } {
  if (!projection.policyId) {
    return false
  }
  if (!projection.patientExists) {
    return false
  }
  if (projection.inSync !== true) {
    return false
  }
  if (projection.policyStatus !== 'CONFIRMED') {
    return false
  }
  if (!isBridgePolicyValidatedWithinReuseWindow(projection.policyValidatedAt)) {
    return false
  }
  return true
}

export function bridgePatientProjectionHasCapturedConsents(
  projection: BridgePatientProjection,
): boolean {
  const { consent } = projection
  return Boolean(consent.consentAobId && consent.consentTreatId && consent.consentPrivacyId)
}

export async function fetchBridgePatientProjection(
  customerId: CustomerId,
): Promise<BridgePatientProjection | null> {
  const ref = doc(firestore, 'customers', customerId, 'projections', 'bridgePatient')
  const snapshot = await getDoc(ref)
  if (!snapshot.exists()) {
    return null
  }
  const parsed = bridgePatientProjectionSchema.safeParse({
    id: snapshot.id,
    ...snapshot.data(),
  })
  return parsed.success ? parsed.data : null
}

export async function readReusableBridgePolicyFromProjection(
  customerId: CustomerId,
): Promise<BridgePolicyId | null> {
  const projection = await fetchBridgePatientProjection(customerId)
  if (!projection || !isBridgePatientProjectionLocallyReusable(projection)) {
    return null
  }
  return projection.policyId
}

async function waitForPolicyConfirmed(policyId: BridgePolicyId): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false
    let remitInFlight = false
    const subscription: { unsubscribe?: () => void } = {}

    const cleanup = () => {
      if (settled) return
      settled = true
      clearTimeout(timeoutHandle)
      clearInterval(remitIntervalHandle)
      subscription.unsubscribe?.()
    }

    const timeoutHandle = setTimeout(() => {
      cleanup()
      reject(
        new Error(
          'Insurance verification is still in progress. Please wait a moment and try again, or continue as self-pay.',
        ),
      )
    }, DEFAULT_POLICY_CONFIRM_TIMEOUT_MS)

    const remitIntervalHandle = setInterval(() => {
      void (async () => {
        if (settled || remitInFlight) return
        remitInFlight = true
        try {
          const result = await bridgeCallables.resolveBridgePolicyStatus({ policyId })
          if (result.status === 'CONFIRMED') {
            cleanup()
            resolve()
          } else if (isBridgePolicyTerminalStatus(result.status)) {
            cleanup()
            reject(
              new Error(
                `Insurance policy status is ${result.status}. Please try again or continue as self-pay.`,
              ),
            )
          }
        } catch (error) {
          cleanup()
          reject(error instanceof Error ? error : new Error(String(error)))
        } finally {
          remitInFlight = false
        }
      })()
    }, DEFAULT_POLICY_REMIT_INTERVAL_MS)

    subscription.unsubscribe = subscribeBridgePolicy(policyId, ({ policy, error }) => {
      if (settled) return
      if (error) {
        cleanup()
        reject(error)
        return
      }
      const status = policy?.status
      if (status === 'CONFIRMED') {
        cleanup()
        resolve()
        return
      }
      if (status && isBridgePolicyTerminalStatus(status)) {
        cleanup()
        reject(
          new Error(
            `Insurance policy status is ${status}. Please try again or continue as self-pay.`,
          ),
        )
      }
    })

    if (settled) {
      subscription.unsubscribe?.()
    }
  })
}

async function ensurePolicyFreshForAttach(policyId: BridgePolicyId): Promise<void> {
  const result = await bridgeCallables.revalidateBridgePolicy({ policyId })

  if (result.outcome === 'valid') {
    void (async () => {
      try {
        const ref = doc(firestore, 'bridge_policies', policyId)
        const snapshot = await getDoc(ref)
        const policy = snapshot.exists()
          ? bridgePolicySchema.safeParse({ id: snapshot.id, ...snapshot.data() })
          : null
        emitClinicalBookingFact({
          factType: 'policy_reused',
          refs: buildBridgePolicyBookingFactRefs({
            policyId,
            policy: policy?.success ? policy.data : null,
          }),
        })
      } catch (error) {
        console.warn('Failed to emit policy_reused booking fact', error)
      }
    })()
    return
  }

  if (result.outcome === 'revalidating') {
    await waitForPolicyConfirmed(policyId)
    const ref = doc(firestore, 'bridge_policies', policyId)
    const snapshot = await getDoc(ref)
    if (!snapshot.exists()) {
      throw new Error('Policy validation is stale or missing after revalidation; please try again')
    }
    const policy = bridgePolicySchema.safeParse({ id: snapshot.id, ...snapshot.data() })
    if (!policy.success || !isBridgePolicyValidatedWithinReuseWindow(policy.data.validatedAt)) {
      throw new Error('Policy validation is stale or missing after revalidation; please try again')
    }
    emitClinicalBookingFact({
      factType: 'policy_rechecked',
      refs: buildBridgePolicyBookingFactRefs({
        policyId,
        policy: policy.data,
      }),
    })
    return
  }

  if (result.outcome === 'invalid') {
    throw new Error(
      'Insurance could not be verified. Check your member ID and try again, or continue as self-pay.',
    )
  }
  if (result.outcome === 'pending') {
    throw new Error(
      'Insurance verification is still in progress. Please wait and try again, or continue as self-pay.',
    )
  }
  throw new Error(
    'Insurance verification could not be completed. Please try again or continue as self-pay.',
  )
}

async function captureBridgeConsentsIfNeeded(customerId: CustomerId): Promise<void> {
  const projection = await fetchBridgePatientProjection(customerId)
  if (projection && bridgePatientProjectionHasCapturedConsents(projection)) {
    return
  }
  await platformCoordApi.captureCustomerConsents({
    consents: {
      bridgeAssignmentOfBenefits: true,
      bridgeConsentToTreat: true,
      bridgePrivacyNotice: true,
    },
  })
}

export async function attachBridgePolicy(args: {
  customerId: CustomerId
  policyId: BridgePolicyId
  signal?: AbortSignal
}): Promise<void> {
  if (args.signal?.aborted) {
    throw new DOMException('Bridge policy create cancelled', 'AbortError')
  }
  await raceAbort(platformCoordApi.attachBridgePolicy({ policyId: args.policyId }), args.signal)
  await waitForBridgePatientPolicyId(args.customerId, args.policyId, {
    signal: args.signal,
  })
  void fetchBridgePatientProjection(args.customerId)
    .then((projection) => {
      emitConsentsBookingFactIfReady(projection)
    })
    .catch((error) => {
      console.warn('Failed to emit consents booking fact', error)
    })
}

export async function tryReuseExistingBridgePolicy(args: {
  customerId: CustomerId
  policyId: BridgePolicyId
}): Promise<void> {
  await captureBridgeConsentsIfNeeded(args.customerId)
  await bridgeCallables.ensureBridgePatient()
  await ensurePolicyFreshForAttach(args.policyId)
  await attachBridgePolicy(args)
}

export const INSURANCE_ON_FILE_CONFIRM_FAILED_MESSAGE = 'We could not confirm policy.'

export type BridgePolicyOnFileDisplay = {
  payerDisplayName: string | null
  sharedAt: Date | null
  payerId: string | null
  memberId: string | null
}

function resolveBridgePolicySharedAt(policy: BridgePolicy): Date | null {
  if (policy.createdAt instanceof Date && !Number.isNaN(policy.createdAt.getTime())) {
    return policy.createdAt
  }
  if (policy.validatedAt instanceof Date && !Number.isNaN(policy.validatedAt.getTime())) {
    return policy.validatedAt
  }
  return null
}

export async function fetchBridgePolicyOnFileDisplay(
  policyId: BridgePolicyId,
): Promise<BridgePolicyOnFileDisplay> {
  const ref = doc(firestore, 'bridge_policies', policyId)
  const snapshot = await getDoc(ref)
  if (!snapshot.exists()) {
    return { payerDisplayName: null, sharedAt: null, payerId: null, memberId: null }
  }
  const parsed = bridgePolicySchema.safeParse({ id: snapshot.id, ...snapshot.data() })
  if (!parsed.success) {
    console.error('Failed to parse bridge policy schema:', parsed.error)
    return { payerDisplayName: null, sharedAt: null, payerId: null, memberId: null }
  }
  const policy = parsed.data
  return {
    payerDisplayName: bridgePolicyPayerDisplayName(policy),
    sharedAt: resolveBridgePolicySharedAt(policy),
    payerId: policy.payerId?.trim() || null,
    memberId: policy.memberId?.trim() || null,
  }
}

export async function fetchBridgePolicyPayerDisplayName(
  policyId: BridgePolicyId,
): Promise<string | null> {
  const display = await fetchBridgePolicyOnFileDisplay(policyId)
  return display.payerDisplayName
}
